"use client";

import Image from "next/image";
import { renderRichText } from "@storyblok/react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { RichText } from "@/components/typography/RichText";

export type ExpertPreviewData = {
  name: string;
  title?: string;
  text?: any;
  image?: { filename: string; alt?: string };
};

type Props = {
  expert: ExpertPreviewData | null;
  onClose: () => void;
};

export function ExpertPreviewModal({ expert, onClose }: Props) {
  if (!expert) return null;

  const bioHtml =
    expert.text && typeof expert.text === "object"
      ? renderRichText(expert.text)
      : typeof expert.text === "string"
        ? expert.text
        : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg p-0 block max-h-[85vh] overflow-y-auto overscroll-contain rounded-lg bg-sand-10 border-0 gap-0" closeClassName="rounded-full bg-sand-10 p-1.5 text-black opacity-100 hover:bg-sand-25 shadow-sm">
        {/* The portrait is shown whole (object-contain) rather than cropped to
            the frame. A blurred, scaled-up copy fills the leftover space so the
            area still reads as one image instead of empty bars. */}
        {expert.image?.filename && (
          <div className="relative w-full aspect-[4/3] overflow-hidden bg-sand-25">
            <Image
              src={expert.image.filename}
              alt=""
              aria-hidden
              fill
              className="object-cover scale-110 blur-xl"
            />
            <Image
              src={expert.image.filename}
              alt={expert.image.alt || expert.name}
              fill
              className="relative object-contain"
            />
          </div>
        )}

        <div className="p-6 space-y-3">
          <DialogTitle className="text-headline-md">
            {expert.name}
          </DialogTitle>

          {expert.title && (
            <p className="text-copy-sm text-gray-75">{expert.title}</p>
          )}

          {bioHtml && (
            <RichText
              html={typeof bioHtml === "string" ? bioHtml : ""}
              className="text-copy"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
